// define constants
import {percentDisplay, color} from "../../../global/map-library";
import {getPerceivedLightness} from "../../../global/color-lightness";

const schoolTypes = {
    "all": "All Schools",
    "ES": "Elementary Schools",
    "HS": "High Schools"
};

const TABS = {
    currentFacility: "nav-facility-tab",
    utilization: "nav-utilization-tab",
    capital: "nav-capital-tab",
};

const pinStyle = {
    elementary: {background: color.blue, borderColor: color.darkBlue},
    highschool: {background: color.red, borderColor: color.darkRed},

    efficient: {background: color.green, borderColor: color.darkGreen},
    underutilized: {background: color.orange, borderColor: color.darkOrange},
    overcrowded: {background: color.blue, borderColor: color.darkBlue},
    na: {background: color.yellow, borderColor: color.darkYellow},

    capitalES: {background: color.blue, borderColor: color.darkBlue},
    capitalHS: {background: color.red, borderColor: color.darkRed},
};

// default options for the charts
const datasetTemplate = {
    borderRadius: 0,
    borderWidth: 0,
    // hoverOffset: 4
};

const USMillionDollar = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 1
});

const USDollar = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0
});

const TABLE_COLUMNS = [
    {
        title: "School",
        tooltipData: "",
        fieldName: "schoolName",
        fieldType: "string",
        tab: [TABS.currentFacility, TABS.utilization, TABS.capital]
    },
    {
        title: "Immediate",
        tooltipData: "Critical facility needs (heating/cooling, roofs, masonry) over the next 5 years based on 2023-24 facility condition assessments.",
        fieldName: "immediateNeed",
        fieldType: "bool",
        tab: [TABS.currentFacility]
    },
    {
        title: "Long Term",
        tooltipData: "Critical facility needs (heating/cooling, roof, masonry) over the next 6-10 years based on 2023-24 facility condition assessments",
        fieldName: "longTermNeed",
        fieldType: "bool",
        tab: [TABS.currentFacility]
    },
    {
        title: "Facility Updates",
        tooltipData: "Improved finishes, lighting, and other classroom upgrades.",
        fieldName: "buildingUpgrades",
        fieldType: "bool",
        tab: [TABS.currentFacility]
    },
    {
        title: "ADA",
        tooltipData: "ADA Accessibility Accessibility needs to make a campus accessible based on the 2021-22 facility Condition assessments. These upgrades will provide equitable access for all.",
        fieldName: "adaCompliance",
        fieldType: "bool",
        tab: [TABS.currentFacility]
    },
    {
        title: "Total",
        fieldName: "currentFacilityTotalMillions",
        fieldType: "currency",
        tab: [TABS.currentFacility]
    },

    {
        title: "Attending SY23-24",
        tooltipData: "Number of students attending based upon school's 20th day enrollment",
        fieldName: "totalAttendingSy202223",
        fieldType: "number",
        tab: [TABS.utilization]
    },
    {
        title: "Ideal Capacity",
        tooltipData: 'School\'s Ideal Capacity based upon total permanent, leased, and modular classrooms.',
        fieldName: 'idealCapacity',
        fieldType: 'number',
        tab: [TABS.utilization]
    },
    {
        title: 'Square Feet',
        tooltipData: 'Total square footage of buildings that make up the campus',
        fieldName: 'squareFootage',
        fieldType: 'number',
        tab: [TABS.utilization]
    },
    {
        title: 'Adjusted Utilization',
        tooltipData: 'Space Utilization Rate based on Adjusted Ideal Capacity and Adjusted Enrollment',
        fieldName: 'adjustedUtilization',
        fieldType: 'percent',
        tab: [TABS.utilization]
    },
    /*
        {
            title: 'Adjusted Ideal Capacity',
            tooltipData: 'School's Ideal Capacity after qualifying classrooms are removed',
            fieldName: '',
            fieldType: '',
            tab:
        },
    */
    {
        title: 'Status',
        tooltipData: 'Space Utilization Status for SY2026',
        fieldName: 'utilizationStatus',
        fieldType: 'string',
        tab: [TABS.utilization]
    },
    {
        title: 'Exterior',
        fieldName: 'exteriorEnvelope',
        fieldType: 'bool',
        tab: [TABS.capital]
    },
    {
        title: 'Interior',
        fieldName: 'interior',
        fieldType: 'bool',
        tab: [TABS.capital]
    },
    {
        title: 'MEP+FP',
        fieldName: 'mepFp',
        fieldType: 'bool',
        tab: [TABS.capital]
    },
    {
        title: 'New Construction',
        fieldName: 'newConstruction',
        fieldType: 'bool',
        tab: [TABS.capital]
    },
    {
        title: 'Site',
        fieldName: 'site',
        fieldType: 'bool',
        tab: [TABS.capital]
    },
    {
        title: 'Total',
        fieldName: 'capitalProjectGrandTotal',
        fieldType: 'currency',
        tab: [TABS.capital]
    },
];

// plugin for displaying totals in the donut chart center
const pluginCenterTotal = {
    id: "center_canvas_total",
    beforeDraw: (chart, args, options) => {
        const {ctx} = chart;
        ctx.save();
        const canvasBounds = ctx.canvas.getBoundingClientRect();
        let radius;
        if (chart.getDatasetMeta(0).controller) {
            radius = chart.getDatasetMeta(0).controller.outerRadius;
        }
        const fontSz = radius * 0.22;
        chart.ctx.textBaseline = "middle";
        chart.ctx.textAlign = "center";
        chart.ctx.font = fontSz + "px Roboto Condensed";
        chart.ctx.fillStyle = color.dark;
        chart.ctx.fillText(options.total, canvasBounds.width / 2, radius);
        ctx.restore();
    }, defaults: {
        total: " "
    }
};

const donutChartOptionsTemplate = {
    type: "doughnut",
    options: {
        plugins: {
            labels: [{
                fontFamily: "Roboto Condensed",
            }],
            legend: {
                display: true, position: "bottom", onClick: null, labels: {
                    fontSize: 12, boxWidth: 12, boxHeight: 12,
                },
            },
            tooltip: {
                callbacks: {
                    label: context => ` ${percentDisplay(context.formattedValue)} ${context.chart.data.labels[context.dataIndex]}`
                }
            },
            datalabels: {
                font: {
                    family: "Roboto Condensed",
                },
                formatter: (value, context) => {
                    return value > 0 ? percentDisplay(value) : "";
                },
                color: (context, args) => {
                    return getPerceivedLightness(context.dataset.backgroundColor[context.dataIndex]) < 70 ? color.light : color.dark;
                }
            }
        },
        animation: false
    },
    plugins: [pluginCenterTotal],
};

export {
    TABS,
    pinStyle,
    USDollar,
    USMillionDollar,
    datasetTemplate,
    TABLE_COLUMNS,
    schoolTypes,
    donutChartOptionsTemplate,
};
